import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2, Video, X } from 'lucide-react';
import { fetchUrlImages, fetchUrlVideos } from '../../api/posts/fetch-post.api';
import { AdAccount } from '../../api/ads/ad-account.api';
import {
  BrandCampaign,
  BrandCampaignVisibility,
  BrandContentFormat,
  MediaRef,
  SaveBrandCampaignBody,
  createBrandCampaign,
  updateBrandCampaign,
} from '../../api/payment/brand-deal.api';
import {
  CONTENT_FORMAT_OPTIONS,
  CONTENT_FORMAT_REQUIRED_MESSAGE,
  contentFormatHint,
  validateContentFormat,
} from '../../utils/brand-content-format';
import { validateProposalCampaignForm, PROPOSAL_LIMITS } from '../../utils/brand-proposal';
import {
  BRAND_CAMPAIGN_LIMITS,
  BRAND_LISTING_LIMITS,
  priceRangeLabel,
  renderCaption,
  validateBrandCampaignForm,
  validateDealPrice,
  validateListingForm,
} from '../../utils/brand-deal';
import { fetchAllCategory } from '../../api/user-profile/user-profile.api';
import { ICategory } from '../../models';
import { useBrandDealLimits } from '../../hooks/use-brand-deal-limits';
import CampaignInvitePicker, { InvitePick } from './campaign-invite-picker';
import {
  BRIEF_FORM_LIMITS,
  DEFAULT_MAX_REVISIONS,
  MAX_REVISION_OPTIONS,
  MAX_REVISIONS_LOCKED_HINT,
  REVIEW_DAYS_SETTING_KEY,
  clampReviewDays,
  displayReviewDays,
  reviewWindowHint,
  validateBriefForm,
} from '../../utils/brand-creative';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import BrandPostPreview from './brand-post-preview';
import { showOrderErrorToast } from './order-error-toast';
import { approxCaLabel, EscrowPart, holdNote, ordererAccountLabel } from '../../utils/individual-order';
import { useOrderFunding } from '../../hooks/use-order-funding';

// Form tạo/sửa chiến dịch "Hợp tác creator" phía doanh nghiệp (Task 7). Lưu luôn ở trạng thái Draft/giữ
// nguyên trạng thái hiện tại — nộp duyệt (Draft ⇒ PendingReview) là hành động riêng ở màn danh sách.
//
// M3 (Task 7): thêm `kind` — "Creative" dựng "Brief sáng tạo" (tiêu đề, mô tả brief, ảnh tham khảo, link
// đích tuỳ chọn, hạn nộp bài; ẩn caption/HoldDays/video — giá mỗi suất/số suất/người theo dõi/chủ đề/hạn
// nhận suất nhập ở bước "Đăng brief" riêng SAU khi admin duyệt, dùng lại `BrandCampaignListingPanel` M2).

const SAMPLE_LINK = 'https://cab.vn/go/abcd1234';

export const INVITE_REQUIRED_MESSAGE = 'Chọn ít nhất một creator để mời.';

// Khai ngoài component cha để không bị dựng lại (mất trạng thái/re-mount input file) mỗi lần render.
export const MediaThumb: React.FC<{ url: string; isVideo?: boolean; onRemove: () => void }> = ({
  url,
  isVideo,
  onRemove,
}) => (
  <div className="relative size-20 shrink-0 overflow-hidden rounded-lg border ui-divider">
    {isVideo ? (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video src={url} className="size-full object-cover" muted />
    ) : (
      <img src={url} alt="" className="size-full object-cover" />
    )}
    <button
      type="button"
      onClick={onRemove}
      className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
    >
      <X size={12} />
    </button>
  </div>
);

export interface BrandCampaignFormProps {
  /** Có giá trị ⇒ sửa chiến dịch này; null/undefined ⇒ tạo mới. */
  initial?: BrandCampaign | null;
  /** Tài khoản doanh nghiệp Verified của người dùng — chỉ cần chọn khi tạo mới và có nhiều hơn 1. */
  bizAccounts: AdAccount[];
  /** "Direct" (mặc định, M1) | "Creative" (brief sáng tạo, M3) — chỉ áp dụng lúc TẠO MỚI (sửa thì theo
   * đúng `initial.kind`, Kind không đổi được sau khi tạo). */
  kind?: 'Direct' | 'Creative' | 'Proposal';
  onSaved: (campaign: BrandCampaign) => void;
  onCancel: () => void;
  /** M5 (Task 3): chuyển trang cha sang tab/mục "Chờ chấm điểm" — hiện nút "Chấm điểm ngay" trên toast
   * lỗi "Bạn cần chấm điểm…" khi TẠO chiến dịch/brief mới bị chặn vì còn deal chưa chấm (sửa không bị
   * chặn, global-constraints). */
  onGoToRatingsTab?: () => void;
  /** Chỉ khi tạo mới: "Public" kèm tham số sàn (admin duyệt xong tự lên sàn), "Private" kèm danh sách creator
   * mời ngay. Đơn nhận đề xuất luôn công khai. */
  visibility?: BrandCampaignVisibility;
}

const BrandCampaignForm: React.FC<BrandCampaignFormProps> = ({
  initial,
  bizAccounts,
  kind,
  onSaved,
  onCancel,
  onGoToRatingsTab,
  visibility,
}) => {
  const effectiveKind = initial?.kind ?? kind ?? 'Direct';
  const isCreative = effectiveKind === 'Creative';
  const isProposal = effectiveKind === 'Proposal';
  // Công khai/riêng tư chỉ chọn lúc tạo; sửa chiến dịch giữ nguyên form cũ.
  const createVisibility: BrandCampaignVisibility | undefined = initial
    ? undefined
    : isProposal
    ? 'Public'
    : visibility;
  const isPublicCreate = createVisibility === 'Public';
  const isPrivateCreate = createVisibility === 'Private';
  // Brief và đơn nhận đề xuất cùng dạng "mô tả yêu cầu": không caption/video/giữ bài, có số lần sửa, ảnh tham khảo tuỳ chọn.
  const isBriefLike = isCreative || isProposal;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [caption, setCaption] = useState(initial?.caption ?? '');
  const [destinationUrl, setDestinationUrl] = useState(initial?.destinationUrl ?? '');
  const [holdDays, setHoldDays] = useState(initial?.holdDays ?? 14);
  const [images, setImages] = useState<MediaRef[]>(initial?.images ?? []);
  const [videos, setVideos] = useState<MediaRef[]>(initial?.videos ?? []);
  const [briefText, setBriefText] = useState(initial?.briefText ?? '');
  const [submitDays, setSubmitDays] = useState(initial?.submitDays ?? 7);
  const [maxRevisions, setMaxRevisions] = useState<number>(initial?.maxRevisions ?? DEFAULT_MAX_REVISIONS);
  const { get: getSetting } = useFeatureFlags();
  // Brief đang sửa: số ngày đã chốt vào brief (brief cũ = 2 ngày). Tạo mới: cài đặt hiện hành, kẹp 3–5 như payment.
  const reviewDays = initial ? displayReviewDays(initial.reviewDays) : clampReviewDays(getSetting(REVIEW_DAYS_SETTING_KEY));
  const revisionsLocked = !!initial?.hasDeals;
  // Chiến dịch mới bắt buộc chọn định dạng; chiến dịch cũ để trống thì vẫn lưu được (máy chủ giữ nguyên).
  const [contentFormat, setContentFormat] = useState<BrandContentFormat | ''>(initial?.contentFormat ?? '');
  const [adAccountId, setAdAccountId] = useState<string>(bizAccounts[0]?.id ?? '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const limits = useBrandDealLimits();
  const [slotPriceVnd, setSlotPriceVnd] = useState(500000);
  const [slotsTotal, setSlotsTotal] = useState(10);
  const [minFollowers, setMinFollowers] = useState(0);
  const [listingApplyDays, setListingApplyDays] = useState<number>(BRAND_LISTING_LIMITS.defaultApplyDays);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [invites, setInvites] = useState<InvitePick[]>([]);
  // Câu tiền theo nguồn tiền của tài khoản đang chọn (ví doanh nghiệp VND hoặc Ca nạp của cá nhân), cùng
  // cách nói với khung "Đăng lên sàn". Chỉ tải số dư khi đang tạo Công khai/Riêng tư (có câu tiền để hiện).
  const selectedAccount = createVisibility ? bizAccounts.find((a) => a.id === adAccountId) ?? null : null;
  const { funding } = useOrderFunding(selectedAccount);
  const listingParts: EscrowPart[] = [{ unitVnd: slotPriceVnd, count: slotsTotal }];
  const inviteParts: EscrowPart[] = invites.map((i) => ({ unitVnd: Number(i.priceVnd) || 0, count: 1 }));

  useEffect(() => {
    if (!isPublicCreate) return;
    let alive = true;
    fetchAllCategory()
      .then((r: ICategory[]) => alive && setCategories(r || []))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [isPublicCreate]);

  const toggleCategory = (id: string) =>
    setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const brandName = initial?.brandName || bizAccounts.find((a) => a.id === adAccountId)?.businessName || '';

  const onPickImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = BRAND_CAMPAIGN_LIMITS.maxImages - images.length;
    if (remaining <= 0) {
      toast.error(`Tối đa ${BRAND_CAMPAIGN_LIMITS.maxImages} ảnh.`);
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    try {
      setUploadingImage(true);
      const fd = new FormData();
      picked.forEach((f) => fd.append('type', f));
      const res = (await fetchUrlImages(fd)) as MediaRef[];
      if (res?.length) setImages((prev) => [...prev, ...res.map((r) => ({ id: r.id, url: r.url }))]);
    } catch {
      toast.error('Tải ảnh thất bại.');
    } finally {
      setUploadingImage(false);
    }
  };

  const onPickVideo = async (file?: File) => {
    if (!file) return;
    if (videos.length >= BRAND_CAMPAIGN_LIMITS.maxVideos) {
      toast.error(`Tối đa ${BRAND_CAMPAIGN_LIMITS.maxVideos} video.`);
      return;
    }
    try {
      setUploadingVideo(true);
      const fd = new FormData();
      fd.append('type', file);
      // Backend trả từng file kèm status; video vượt dung lượng → status 'Failed' + lý do rõ.
      const res = (await fetchUrlVideos(fd)) as Array<{ id?: string; url?: string; status?: string; error?: string }>;
      const failed = res.filter((v) => v.status === 'Failed' || !v.id);
      const ok = res.filter((v) => v.status !== 'Failed' && v.id);
      if (failed.length) toast.error(failed[0]?.error || 'Tải video thất bại.');
      if (ok.length) setVideos((prev) => [...prev, ...ok.map((v) => ({ id: v.id as string, url: v.url as string }))]);
    } catch {
      toast.error('Tải video thất bại.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const submit = async () => {
    const error = isProposal
      ? validateProposalCampaignForm({ title, wish: briefText, maxRevisions, images: images.length, destinationUrl })
      : isCreative
      ? validateBriefForm({ title, briefText, submitDays, destinationUrl, images: images.length, maxRevisions })
      : validateBrandCampaignForm({
          title,
          caption,
          destinationUrl,
          holdDays,
          images: images.length,
          videos: videos.length,
          contentFormat: contentFormat || null,
        });
    if (error) {
      toast.error(error);
      return;
    }
    const formatError =
      !initial && !contentFormat
        ? CONTENT_FORMAT_REQUIRED_MESSAGE
        : isBriefLike
        ? null
        : validateContentFormat(contentFormat || null, caption, images.length, videos.length);
    if (formatError) {
      toast.error(formatError);
      return;
    }
    if (!initial && !adAccountId) {
      toast.error('Chọn tài khoản doanh nghiệp để tạo chiến dịch.');
      return;
    }
    if (isPublicCreate) {
      const listingError = validateListingForm(
        { slotPriceVnd, slotsTotal, applyDays: listingApplyDays },
        limits.minPriceVnd
      );
      if (listingError) {
        toast.error(listingError);
        return;
      }
      if (!Number.isInteger(minFollowers)) {
        toast.error('Số người theo dõi phải là số nguyên.');
        return;
      }
    }
    if (isPrivateCreate) {
      if (invites.length === 0) {
        toast.error(INVITE_REQUIRED_MESSAGE);
        return;
      }
      if (invites.some((i) => !Number.isInteger(i.priceVnd))) {
        toast.error('Giá phải là số nguyên.');
        return;
      }
      const priceError = invites
        .map((i) => validateDealPrice(i.priceVnd, limits.minPriceVnd, limits.maxPriceVnd))
        .find(Boolean);
      if (priceError) {
        toast.error(priceError);
        return;
      }
    }
    const baseBody: SaveBrandCampaignBody = isProposal
      ? {
          adAccountId: initial ? undefined : adAccountId,
          title: title.trim(),
          caption: '',
          destinationUrl: destinationUrl.trim(),
          holdDays: 0,
          images,
          videos: [],
          kind: 'Proposal',
          briefText: briefText.trim(),
          maxRevisions,
          contentFormat: contentFormat || undefined,
        }
      : isCreative
      ? {
          adAccountId: initial ? undefined : adAccountId,
          title: title.trim(),
          caption: '',
          destinationUrl: destinationUrl.trim(),
          holdDays: 0,
          images,
          videos: [],
          kind: 'Creative',
          briefText: briefText.trim(),
          submitDays,
          maxRevisions,
          contentFormat: contentFormat || undefined,
        }
      : {
          adAccountId: initial ? undefined : adAccountId,
          title: title.trim(),
          caption: caption.trim(),
          destinationUrl: destinationUrl.trim(),
          holdDays,
          images,
          videos,
          contentFormat: contentFormat || undefined,
        };
    const body: SaveBrandCampaignBody = isPublicCreate
      ? {
          ...baseBody,
          visibility: 'Public',
          slotPriceVnd,
          slotsTotal,
          minFollowers,
          categoryIds,
          listingApplyDays,
        }
      : isPrivateCreate
      ? {
          ...baseBody,
          visibility: 'Private',
          invites: invites.map((i) => ({ creatorId: i.creatorId, priceVnd: i.priceVnd })),
        }
      : baseBody;
    try {
      setSubmitting(true);
      const saved = initial ? await updateBrandCampaign(initial.id, body) : await createBrandCampaign(body);
      if (isPrivateCreate) {
        const results = saved.inviteResults ?? [];
        const failed = results.filter((r) => !r.ok);
        const okCount = results.filter((r) => r.ok).length;
        const nameOf = (id: string) => invites.find((i) => i.creatorId === id)?.displayName || id;
        failed.forEach((r) =>
          toast.error(`Không mời được ${nameOf(r.creatorId)}: ${r.error || 'lỗi không rõ'}`, { duration: 10000 })
        );
        if (okCount > 0) toast.success(`Đã gửi ${okCount} lời mời.`);
        else if (!failed.length) toast.success('Đã tạo chiến dịch.');
        onSaved(saved);
        return;
      }
      toast.success(
        initial
          ? 'Đã lưu thay đổi.'
          : isPublicCreate
          ? 'Đã gửi duyệt. Admin duyệt xong chiến dịch tự lên sàn.'
          : isProposal
          ? 'Đã tạo đơn nhận đề xuất (nháp), nộp duyệt khi sẵn sàng.'
          : isCreative
          ? 'Đã tạo brief sáng tạo (nháp) — nộp duyệt khi sẵn sàng.'
          : 'Đã tạo chiến dịch (nháp) — nộp duyệt khi sẵn sàng.'
      );
      onSaved(saved);
    } catch (e) {
      showOrderErrorToast(e, isProposal ? 'Lưu đơn nhận đề xuất thất bại.' : isCreative ? 'Lưu brief thất bại.' : 'Lưu chiến dịch thất bại.', onGoToRatingsTab);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {!initial && bizAccounts.length > 1 && (
        <label className="block text-sm ui-text">
          Tài khoản đặt đơn
          <select
            className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
            value={adAccountId}
            onChange={(e) => setAdAccountId(e.target.value)}
          >
            {bizAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {ordererAccountLabel(a)}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="block text-sm ui-text">
        Tiêu đề
        <input
          className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={BRAND_CAMPAIGN_LIMITS.titleMaxLength}
          placeholder="VD: Ra mắt sản phẩm X"
        />
      </label>

      <div className="text-sm ui-text">
        <label htmlFor="campaign-content-format" className="block">
          Định dạng nội dung
        </label>
        <select
          id="campaign-content-format"
          className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text disabled:opacity-60"
          value={contentFormat}
          disabled={revisionsLocked}
          onChange={(e) => setContentFormat(e.target.value as BrandContentFormat | '')}
        >
          <option value="" disabled>
            Chọn định dạng
          </option>
          {CONTENT_FORMAT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {contentFormat && <span className="mt-1 block text-[11px] ui-muted">{contentFormatHint(contentFormat)}</span>}
      </div>

      {isProposal ? (
        <label className="block text-sm ui-text">
          Mô tả mong muốn
          <textarea
            className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
            rows={6}
            value={briefText}
            onChange={(e) => setBriefText(e.target.value)}
            maxLength={PROPOSAL_LIMITS.wishMaxLength}
            placeholder="Bạn muốn creator đề xuất nội dung thế nào: sản phẩm, thông điệp, phong cách… (20–2.000 ký tự)"
          />
          <span className="mt-1 block text-[11px] ui-muted">{briefText.length}/{PROPOSAL_LIMITS.wishMaxLength}</span>
        </label>
      ) : isCreative ? (
        <label className="block text-sm ui-text">
          Mô tả yêu cầu (brief)
          <textarea
            className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
            rows={6}
            value={briefText}
            onChange={(e) => setBriefText(e.target.value)}
            maxLength={BRIEF_FORM_LIMITS.briefTextMaxLength}
            placeholder="Mô tả rõ yêu cầu nội dung, phong cách, thông điệp… để creator soạn bài đúng ý (20–3.000 ký tự)"
          />
          <span className="mt-1 block text-[11px] ui-muted">{briefText.length}/{BRIEF_FORM_LIMITS.briefTextMaxLength}</span>
        </label>
      ) : (
        <label className="block text-sm ui-text">
          Nội dung bài đăng (caption)
          <textarea
            className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
            rows={4}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={BRAND_CAMPAIGN_LIMITS.captionMaxLength}
            placeholder="Nội dung bài creator sẽ đăng..."
          />
          <span className="mt-1 block text-[11px] ui-muted">
            Gõ <code>{'{link}'}</code> ở chỗ muốn chèn link theo dõi riêng từng creator — không gõ thì link sẽ tự
            gắn thêm ở cuối bài.
          </span>
        </label>
      )}

      <label className="block text-sm ui-text">
        Link đích{isBriefLike ? ' (tuỳ chọn)' : ' (khi người xem bấm vào link)'}
        <input
          className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
          value={destinationUrl}
          onChange={(e) => setDestinationUrl(e.target.value)}
          maxLength={BRAND_CAMPAIGN_LIMITS.destinationUrlMaxLength}
          placeholder="https://..."
        />
      </label>

      {isProposal ? null : isCreative ? (
        <label className="block text-sm ui-text">
          Hạn nộp bài ({BRIEF_FORM_LIMITS.minSubmitDays}–{BRIEF_FORM_LIMITS.maxSubmitDays} ngày, tính từ lúc creator
          nhận brief)
          <input
            type="number"
            className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
            value={submitDays}
            min={BRIEF_FORM_LIMITS.minSubmitDays}
            max={BRIEF_FORM_LIMITS.maxSubmitDays}
            onChange={(e) => setSubmitDays(Number(e.target.value))}
          />
        </label>
      ) : (
        <label className="block text-sm ui-text">
          Số ngày giữ bài ({BRAND_CAMPAIGN_LIMITS.minHoldDays}–{BRAND_CAMPAIGN_LIMITS.maxHoldDays} ngày, tính từ lúc
          creator đăng bài)
          <input
            type="number"
            className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
            value={holdDays}
            min={BRAND_CAMPAIGN_LIMITS.minHoldDays}
            max={BRAND_CAMPAIGN_LIMITS.maxHoldDays}
            onChange={(e) => setHoldDays(Number(e.target.value))}
          />
        </label>
      )}

      {isBriefLike && (
        <div className="text-sm ui-text">
          <label htmlFor="brief-max-revisions" className="block">
            Số lần được yêu cầu sửa
          </label>
          <select
            id="brief-max-revisions"
            className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text disabled:opacity-60"
            value={maxRevisions}
            disabled={revisionsLocked}
            onChange={(e) => setMaxRevisions(Number(e.target.value))}
          >
            {MAX_REVISION_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n === 0 ? '0 lần (chỉ duyệt hoặc từ chối)' : `${n} lần`}
              </option>
            ))}
          </select>
          {revisionsLocked && <span className="mt-1 block text-[11px] ui-muted">{MAX_REVISIONS_LOCKED_HINT}</span>}
          <span className="mt-1 block text-[11px] ui-muted">{reviewWindowHint(reviewDays)}</span>
        </div>
      )}

      <div>
        <div className="mb-1 text-sm ui-text">
          Ảnh tham khảo (tối đa {BRAND_CAMPAIGN_LIMITS.maxImages}){isBriefLike ? ' — tuỳ chọn' : ''}
        </div>
        <div className="flex flex-wrap gap-2">
          {images.map((img) => (
            <MediaThumb
              key={img.id || img.url}
              url={img.url}
              onRemove={() => setImages((prev) => prev.filter((x) => x !== img))}
            />
          ))}
          {images.length < BRAND_CAMPAIGN_LIMITS.maxImages && (
            <label className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed ui-divider">
              {uploadingImage ? (
                <Loader2 size={18} className="animate-spin ui-muted" />
              ) : (
                <ImagePlus size={18} className="ui-muted" />
              )}
              <input type="file" accept="image/*" multiple hidden onChange={(e) => onPickImages(e.target.files)} />
            </label>
          )}
        </div>
      </div>

      {!isBriefLike && (
        <div>
          <div className="mb-1 text-sm ui-text">Video (tối đa {BRAND_CAMPAIGN_LIMITS.maxVideos})</div>
          <div className="flex flex-wrap gap-2">
            {videos.map((v) => (
              <MediaThumb
                key={v.id || v.url}
                url={v.url}
                isVideo
                onRemove={() => setVideos((prev) => prev.filter((x) => x !== v))}
              />
            ))}
            {videos.length < BRAND_CAMPAIGN_LIMITS.maxVideos && (
              <label className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed ui-divider">
                {uploadingVideo ? (
                  <Loader2 size={18} className="animate-spin ui-muted" />
                ) : (
                  <Video size={18} className="ui-muted" />
                )}
                <input type="file" accept="video/*" hidden onChange={(e) => onPickVideo(e.target.files?.[0])} />
              </label>
            )}
          </div>
        </div>
      )}

      {!isBriefLike && (
        <div>
          <div className="mb-1 text-sm font-semibold ui-text">Xem trước bài đăng</div>
          <BrandPostPreview
            title={title || undefined}
            captionWithLink={renderCaption(caption || '(chưa nhập nội dung)', SAMPLE_LINK)}
            images={images}
            videos={videos}
            brandName={brandName}
            brandVerified
          />
        </div>
      )}

      {isPublicCreate && (
        <div className="space-y-3 rounded-xl border ui-divider p-4">
          <div className="font-semibold ui-text">Đăng lên Sàn chiến dịch</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm ui-text">
              Giá mỗi suất ({priceRangeLabel(limits.minPriceVnd)})
              <input
                type="number"
                value={slotPriceVnd}
                onChange={(e) => setSlotPriceVnd(Number(e.target.value))}
                className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
              />
            </label>
            <label className="block text-sm ui-text">
              Số suất ({BRAND_LISTING_LIMITS.minSlots}–{BRAND_LISTING_LIMITS.maxSlots})
              <input
                type="number"
                value={slotsTotal}
                onChange={(e) => setSlotsTotal(Number(e.target.value))}
                className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
              />
            </label>
            <label className="block text-sm ui-text">
              Hạn nhận đơn ({BRAND_LISTING_LIMITS.minApplyDays}–{BRAND_LISTING_LIMITS.maxApplyDays} ngày)
              <input
                type="number"
                value={listingApplyDays}
                onChange={(e) => setListingApplyDays(Number(e.target.value))}
                className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
              />
            </label>
            <label className="block text-sm ui-text">
              Người theo dõi tối thiểu (0 = không yêu cầu)
              <input
                type="number"
                min={0}
                value={minFollowers}
                onChange={(e) => setMinFollowers(Math.max(0, Number(e.target.value)))}
                className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
              />
            </label>
          </div>
          <div>
            <div className="mb-1 text-sm ui-text">Chủ đề (bỏ trống = mọi chủ đề)</div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCategory(c.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    categoryIds.includes(c.id) ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <p className="text-[12px] ui-text">
            {`Admin duyệt xong chiến dịch tự lên sàn. ${holdNote(funding, listingParts)} lúc lên sàn.`}
          </p>
          {funding.kind === 'Individual' && (
            <p className="text-[12px] font-semibold text-[#0057FF] dark:text-blue-300">
              {`${approxCaLabel(listingParts, funding.vndPerCa)} cho ${slotsTotal} suất`}
            </p>
          )}
        </div>
      )}

      {isPrivateCreate && (
        <div className="space-y-3 rounded-xl border ui-divider p-4">
          <div className="font-semibold ui-text">Creator được mời</div>
          <p className="text-xs ui-muted">
            Giá mỗi creator từ {priceRangeLabel(limits.minPriceVnd, limits.maxPriceVnd)}. Lời mời gửi đi ngay khi
            tạo chiến dịch.
          </p>
          <CampaignInvitePicker adAccountId={adAccountId || undefined} value={invites} onChange={setInvites} />
          <p className="text-[12px] font-semibold ui-text">{`${holdNote(funding, inviteParts)} khi gửi lời mời.`}</p>
          {funding.kind === 'Individual' && invites.length > 0 && (
            <p className="text-[12px] font-semibold text-[#0057FF] dark:text-blue-300">
              {`${approxCaLabel(inviteParts, funding.vndPerCa)} cho ${invites.length} creator`}
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          className="rounded-lg bg-[#0057FF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {submitting
            ? 'Đang lưu…'
            : isPublicCreate
            ? 'Gửi duyệt'
            : isPrivateCreate
            ? 'Tạo và gửi lời mời'
            : isProposal
            ? 'Lưu đơn'
            : isCreative
            ? 'Lưu brief'
            : 'Lưu chiến dịch'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border ui-divider px-4 py-2 text-sm font-semibold ui-text hover:bg-black/5 dark:hover:bg-white/5"
        >
          Huỷ
        </button>
      </div>
    </div>
  );
};

export default BrandCampaignForm;
